import React, { useState, useEffect, useRef, useMemo } from "react";
import Header from "../../component/menu/Header";
import { constants } from "../../constant/constants";
import SideMenu from "../../component/menu/SideMenu";
import useUserStore from "../../stores/store";
import SalesReportComponent from "../../component/restaurants/reports/SalesReportComponent";
import { ReportService } from "../../services/reports-services";
import CustomLoader from "../../component/menu/CustomLoader";

export default function SalesReport() {
  const { user, restaurantId, showNotification, networkCall, setNetworkCall } = useUserStore();
  const [salesSummary, setSalesSummary] = useState({});
  const [salesReportList, setSalesReportList] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItem, setTotalItem] = useState(0);
  const [salesFilter, setSalesFilter] = useState({
    restaurantId: restaurantId,
    orderNo: "",
    fromDateTime: "",
    toDateTime: "",
    paymentReceivedTypes: [],
    takeoutTypes: [],
  });
  const [isFilterClear, setIsFilterClear] = useState(false);
  const [summationData, setSummationData] = useState(null);
  const [paymentReceivedTypes, setPaymentReceivedTypes] = useState([]);
  const [takeoutTypes, setTakeoutTypes] = useState([]);

  // fetch from reports list api
  async function fetchSalesReportList(filter) {
    setNetworkCall(true);
    try {
      const salesList = await ReportService.getSalesReportList(filter, user.token.token);
      setSalesReportList(ReportService.formatDataList(salesList.data));
      setPage(salesList.page);
      setPerPage(salesList.perPage);
      setTotalItem(salesList.total);
      setNetworkCall(false);
    } catch (err) {
      showNotification("Something went wrong in serverside. Try again later.");
      setNetworkCall(false);
      return;
    }
  }

  // fetch from reports summations api
  async function fetchSalesReportSummation(filter) {
    setNetworkCall(true);
    try {
      const reportSummation = await ReportService.getSalesReportSummation(filter, user.token.token);
      const { data } = reportSummation;
      setSummationData(data);
      setNetworkCall(false);
    } catch (err) {
      showNotification("Something went wrong in serverside. Try again later.");
      setNetworkCall(false);
      return;
    }
  }

  // fetch from summary reports api
  async function fetchSummaryReport() {
    try {
      const summaryReport = await ReportService.getSalesReportSummary(restaurantId, user.token.token);
      setPaymentReceivedTypes(summaryReport.paymentReceivedTypes.map(item => ({ ...item, id: crypto.randomUUID() })));
      setTakeoutTypes(summaryReport.takeoutTypes.map(item => ({ ...item, id: crypto.randomUUID() })));
      setSalesSummary(summaryReport.summary);
    } catch (error) {
      showNotification("Something went wrong. Try again later.");
      return;
    }
  }

  // initial loading
  useEffect(() => {
    async function fetchSummationData() {
      await fetchSalesReportSummation(salesFilter);
    }

    fetchSummaryReport();
    fetchSummationData();
    fetchSalesReportList(salesFilter);
  }, [restaurantId]);

  // while filter is clear
  useEffect(() => {
    async function fetchData() {
      if (isFilterClear) {
        await fetchSalesReportList(salesFilter);
        await fetchSalesReportSummation(salesFilter);
        setIsFilterClear(false);
      }
    }

    fetchData();
  }, [isFilterClear]);

  function onHandleFilterData() {
    if (page !== 1 || perPage !== 10) {
      setPage(1);
      setPerPage(10);
    }
    const tempFilter = { ...salesFilter, page: 1, perPage: 10 };
    fetchSalesReportList(tempFilter);
    fetchSalesReportSummation({ ...salesFilter });
  }

  function onHandlePageChange(page, pageSize) {
    const filter = { ...salesFilter, page: page, perPage: pageSize };
    fetchSalesReportList(filter);
    fetchSalesReportSummation(filter);
    setPage(page);
    setPerPage(pageSize);
  }

  function onHandlePageSizeChange(current, size) {
    const filter = { ...salesFilter, page: page, perPage: size };
    fetchSalesReportList(filter);
    fetchSalesReportSummation(filter);
    setPerPage(size);
  }

  function onHandleDownloadReport() {
    setNetworkCall(true);
    ReportService.getDownloadSalesReport(salesFilter, user.token.token).then((res) => {
      if (res.data) {
        setNetworkCall(false);
        ReportService.downloadPDF(res.data, "sales-report.pdf");
        showNotification('Sales report downloaded successfully!', false);
      } else {
        setNetworkCall(false);
        showNotification("Failed to download the report");
        return;
      }
    });
  }

  return (
    <section>
      <div className="flex flex-col">
        <CustomLoader />
        <Header title={""} menu={constants.MENU.SALES_REPORT} currentMenuKey={"T_6_S_6_L_1"} />
        <div className="flex flex-row bg-prewhite px-[12%]">
          <SideMenu title={`Sale Report`} menu={constants.MENU.SALES_REPORT} />
        </div>
        <div className=" mt-[80px] ml-[80px] px-[50px]">
          <h1 className="pt-[50px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
            {constants.MENU.REPORTS[1]}
          </h1>

          <SalesReportComponent
            paymentReceivedOptions={paymentReceivedTypes}
            takeoutOptions={takeoutTypes}
            summaryData={salesSummary}
            summationData={summationData}
            reportData={salesReportList}
            setSalesFilter={setSalesFilter}
            handleFilterApply={onHandleFilterData}
            page={page}
            perPage={perPage}
            setPerPage={setPerPage}
            total={totalItem}
            handlePageChange={onHandlePageChange}
            handlePageSizeChange={onHandlePageSizeChange}
            setIsFilterClear={setIsFilterClear}
            handleDownload={onHandleDownloadReport}
            loading={networkCall}
          />
        </div>
      </div>
    </section>
  );
}
